import { useEffect, useState } from 'react'
import { OnboardingStep } from '@/hooks/use-onboarding'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { OrganisationNameLookupField } from '@/components/onboarding/organisation-name-lookup-field'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import {
  createManagedOrganisation,
  resolveEmployerOnboardingOrgSlug,
} from '@/lib/employer-onboarding-organisation-api'
import { websiteDomain } from '@/lib/organisation-prefill-api'
import { OnboardingStepNext, runOnboardingStepNext } from '@/lib/onboarding-step-nav'

interface EmployerOrganisationStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function EmployerOrganisationStep({
  onNext,
  onBack,
  isCompleting,
  canGoBack,
}: EmployerOrganisationStepProps) {
  const [organisationName, setOrganisationName] = useState('')
  const [existingSlug, setExistingSlug] = useState<string | null>(null)
  const [domain, setDomain] = useState<string | null>(null)
  const [savedDomain, setSavedDomain] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const slug = await resolveEmployerOnboardingOrgSlug()
        if (!slug) return

        const response = await authorizedFetch(
          `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(slug)}/`,
        )
        if (!response.ok) return

        const org = (await response.json()) as { name?: string; slug?: string; website?: string }
        if (!cancelled) {
          const orgDomain = websiteDomain(org.website)
          setExistingSlug(org.slug ?? slug)
          setOrganisationName(org.name ?? '')
          setDomain(orgDomain)
          setSavedDomain(orgDomain)
        }
      } catch (err) {
        console.error('Failed to load organisation:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = async () => {
    const name = organisationName.trim()
    if (!name) {
      setError('Organisation name is required')
      return
    }

    setSaving(true)
    setError(null)

    const pickedWebsite = domain && domain !== savedDomain ? { website: `https://${domain}` } : {}

    try {
      if (existingSlug) {
        const response = await authorizedFetch(
          `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(existingSlug)}/`,
          {
            method: 'PUT',
            body: JSON.stringify({ name, ...pickedWebsite }),
          },
        )
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            (typeof errorData.error === 'string' && errorData.error) ||
              'Failed to update organisation',
          )
        }
      } else {
        await createManagedOrganisation(name, pickedWebsite)
      }
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      console.error('Error saving organisation:', err)
      setError(err instanceof Error ? err.message : 'Failed to save organisation')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading your organisation…</p>
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <OrganisationNameLookupField
          id="org-name"
          value={organisationName}
          onChange={setOrganisationName}
          domain={domain}
          onDomainChange={setDomain}
          disabled={saving || isCompleting}
        />

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleSave}
        backDisabled={saving || isCompleting}
        continueDisabled={saving || isCompleting || !organisationName.trim()}
        isBusy={saving}
        continueBusyLabel="Saving..."
      />
    </div>
  )
}
